import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { usePlanGuard } from '@/hooks/usePlanGuard'
import { Space } from '@/types'
import { db } from '@/config/firebase'
import { collection, query, where, onSnapshot, deleteDoc, doc, orderBy, limit } from 'firebase/firestore'
import { Plus, LayoutGrid, Info, Users, Search, Trash2, Settings2, X, CheckSquare, Square } from 'lucide-react'
import { SPACE_TEMPLATES } from '@/utils/spaceTemplates'
import SpaceForm from '@/components/Spaces/SpaceForm'
import toast from 'react-hot-toast'

// Sub-componente de Card
function SpaceCard({ space, isSelected, onToggle, onEdit, onDelete }: { space: Space, isSelected: boolean, onToggle: (id: string) => void, onEdit: (s: Space) => void, onDelete: (id: string) => void }) {
    const template = SPACE_TEMPLATES[space.type] || SPACE_TEMPLATES.room
    const Icon = template.icon

    return (
        <div
            onClick={() => onToggle(space.id)}
            className={`bg-white p-0 group hover:-translate-y-2 transition-all duration-500 overflow-hidden relative border rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:shadow-[#001c28]/10 cursor-pointer ${isSelected ? 'border-[#d4a94d] ring-2 ring-[#d4a94d] ring-offset-2' : 'border-[#001c28]/5 hover:border-[#d4a94d]'}`}
        >
            <div className="absolute -right-4 -top-4 w-28 h-28 bg-[#d4a94d]/10 rounded-full blur-2xl group-hover:bg-[#d4a94d]/20 transition-colors" />

            {/* Selection Checkbox */}
            <div className="absolute top-4 right-4 z-20">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#d4a94d] border-[#d4a94d]' : 'bg-white/90 border-[#001c28]/10 group-hover:border-[#d4a94d]'}`}>
                    {isSelected && <div className="w-2.5 h-2.5 bg-[#001c28] rounded-sm" />}
                </div>
            </div>

            <div className="relative z-10">
                <div className="aspect-[4/3] relative overflow-hidden bg-[#001c28]/5 rounded-t-[2.5rem]">
                    {space.photos?.[0] ? (
                        <img src={space.photos[0]} alt={space.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#001c28]/10">
                            <Icon className="w-16 h-16" />
                        </div>
                    )}
                    <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-[#001c28] text-[10px] font-black px-3 py-2 rounded-lg uppercase tracking-wider flex items-center shadow-lg border border-[#001c28]/10">
                            <Icon className="w-3 h-3 mr-1.5 text-[#d4a94d]" />
                            {template.name}
                        </span>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="font-black text-[#001c28] group-hover:text-[#d4a94d] transition-colors uppercase tracking-tight text-base leading-tight flex-1">{space.name}</h3>
                        <div className="flex items-center bg-[#001c28]/5 px-3 py-1.5 rounded-lg text-[#001c28] text-xs font-bold border border-[#001c28]/10 shrink-0">
                            <Users className="w-3.5 h-3.5 mr-1.5" />
                            {space.capacity}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 min-h-[2rem]">
                        {space.amenities.slice(0, 3).map((a, i) => (
                            <span key={i} className="text-[10px] bg-[#001c28]/5 text-[#001c28]/60 px-2.5 py-1.5 rounded-md border border-[#001c28]/5 font-black uppercase tracking-wider">
                                {a}
                            </span>
                        ))}
                        {space.amenities.length > 3 && (
                            <span className="text-[10px] text-[#001c28]/40 font-bold px-2 py-1.5">+{space.amenities.length - 3}</span>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(space) }}
                            className="flex-1 py-3 bg-[#001c28] group-hover:bg-[#d4a94d] group-hover:text-[#001c28] text-white text-xs font-black rounded-xl transition-all uppercase tracking-widest border border-[#001c28]/10 group-hover:shadow-lg group-hover:shadow-[#001c28]/20"
                        >
                            Gerenciar
                        </button>
                        {(onDelete) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(space.id);
                                }}
                                className="w-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-colors border border-red-100"
                                title="Excluir Espaço"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function SpacesPage() {
    const { currentOrg } = useAuthStore()
    const { checkSpaceLimit, plan } = usePlanGuard()
    const [spaces, setSpaces] = useState<Space[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [selectedSpace, setSelectedSpace] = useState<Space | null>(null)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [isBatchEdit, setIsBatchEdit] = useState(false)

    const toggleSelect = (id: string) => {
        setSelectedIds(current =>
            current.includes(id)
                ? current.filter(i => i !== id)
                : [...current, id]
        )
    }

    const handleSelectAll = () => {
        if (selectedIds.length === filteredSpaces.length) {
            setSelectedIds([])
        } else {
            setSelectedIds(filteredSpaces.map(s => s.id))
        }
    }

    useEffect(() => {
        if (!currentOrg) return

        const q = query(collection(db, 'spaces'), where('orgId', '==', currentOrg.id))
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setSpaces(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space)))
            setLoading(false)
        })

        return () => unsubscribe()
    }, [currentOrg?.id])

    const canAddMore = checkSpaceLimit(spaces.length)

    const filteredSpaces = spaces.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.type.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const openForm = (space: Space | null = null) => {
        setIsBatchEdit(false)
        setSelectedSpace(space)
        setIsFormOpen(true)
    }

    const handleDeleteSpace = async (spaceId: string) => {
        if (!confirm('Tem certeza que deseja excluir este espaço? Esta ação não pode ser desfeita.')) return

        try {
            setLoading(true)
            await deleteDoc(doc(db, 'spaces', spaceId))
            toast.success('Espaço excluído com sucesso!')
        } catch (error) {
            console.error(error)
            toast.error('Erro ao excluir espaço.')
        } finally {
            setLoading(false)
        }
    }

    const handleBatchDelete = async () => {
        if (!confirm(`Tem certeza que deseja excluir ${selectedIds.length} espaços? Esta ação não pode ser desfeita.`)) return

        setLoading(true)
        try {
            const promises = selectedIds.map(id => deleteDoc(doc(db, 'spaces', id)))
            await Promise.all(promises)
            toast.success(`${selectedIds.length} espaços excluídos com sucesso!`)
            setSelectedIds([]) // Clear selection
        } catch (error) {
            console.error(error)
            toast.error('Erro ao excluir espaços.')
        } finally {
            setLoading(false)
        }
    }



    const [activeTab, setActiveTab] = useState<'spaces' | 'reviews'>('spaces')
    const [globalReviews, setGlobalReviews] = useState<any[]>([])

    useEffect(() => {
        if (!currentOrg || activeTab !== 'reviews') return

        const q = query(
            collection(db, 'reviews'),
            // Ideally filter by org via spaceId, but simplified for now:
            orderBy('createdAt', 'desc'),
            limit(50)
        )
        // Note: Real filtering by Org requires queries on SpaceId lists or denormalized orgId on review.
        // For MVP we assume we fetch recent and filter client side if needed, or if security allows.

        const unsub = onSnapshot(q, (snap) => {
            setGlobalReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        })
        return () => unsub()
    }, [currentOrg, activeTab])

    if (!currentOrg) return null

    return (
        <div className="max-w-6xl w-full mx-auto p-6 space-y-8 page-enter">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b-2 border-[#001c28]/5">
                <div className="space-y-2">
                    <h1 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-[0.9]">Gestão de Espaços</h1>
                    <p className="font-bold text-[#001c28]/60 text-sm max-w-2xl">
                        Configure e gerencie as áreas reserváveis da sua <span className="text-[#001c28] font-black">{currentOrg.name}</span>.
                    </p>

                    <div className="flex items-center space-x-3 pt-2">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/40" />
                            <input
                                type="text"
                                placeholder="Buscar por nome ou tipo..."
                                className="w-full bg-[#001c28]/[0.02] border border-[#001c28]/10 rounded-xl pl-10 pr-4 py-2 text-sm font-bold text-[#001c28] focus:border-[#d4a94d] focus:ring-2 focus:ring-[#d4a94d]/20 transition-all outline-none placeholder:text-[#001c28]/30"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={handleSelectAll}
                            className={`flex items-center space-x-2 text-[10px] font-bold uppercase tracking-wider transition-colors px-3 py-2 rounded-lg border ${selectedIds.length > 0 ? 'border-[#d4a94d] text-[#001c28] bg-[#d4a94d]/10' : 'border-transparent text-[#001c28]/40 hover:text-[#001c28]'}`}
                        >
                            {selectedIds.length > 0 && selectedIds.length === filteredSpaces.length ? (
                                <CheckSquare className="w-4 h-4 text-[#d4a94d]" />
                            ) : (
                                <Square className="w-4 h-4" />
                            )}
                            <span>{selectedIds.length === filteredSpaces.length ? 'Desmarcar' : 'Selecionar Tudo'}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-col items-center md:items-end gap-2">
                    <button
                        onClick={() => {
                            if (!canAddMore) {
                                toast.error(`Você atingiu o limite de ${spaces.length} espaços do plano ${plan.toUpperCase()}`)
                                return
                            }
                            openForm()
                        }}
                        className={`flex items-center space-x-2 px-6 py-3 rounded-xl shadow-lg transition-all transform active:scale-95 text-xs font-black uppercase tracking-widest ${canAddMore
                            ? 'bg-[#d4a94d] text-white hover:bg-[#001c28] shadow-[#d4a94d]/30 hover:shadow-[#001c28]/30'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed border-2 border-slate-200'
                            }`}
                    >
                        <Plus className="w-4 h-4" />
                        <span>Novo Espaço</span>
                    </button>
                    <p className="text-[10px] font-bold text-[#001c28]/40 uppercase tracking-wider">
                        {spaces.length} de {plan === 'ultra' ? '∞' : checkSpaceLimit(1000) ? 'ilimitados' : 'limite'} espaços usados
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-[#001c28]/5 p-2 rounded-2xl w-fit">
                <button
                    onClick={() => setActiveTab('spaces')}
                    className={`px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all ${activeTab === 'spaces'
                        ? 'bg-[#001c28] text-[#d4a94d] shadow-lg'
                        : 'text-[#001c28]/40 hover:bg-[#001c28]/5'
                        }`}
                >
                    Espaços
                </button>
                <button
                    onClick={() => setActiveTab('reviews')}
                    className={`px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all ${activeTab === 'reviews'
                        ? 'bg-[#001c28] text-[#d4a94d] shadow-lg'
                        : 'text-[#001c28]/40 hover:bg-[#001c28]/5'
                        }`}
                >
                    Histórico de Avaliações
                </button>
            </div>

            {/* Content Area */}
            {activeTab === 'spaces' ? (
                loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12">
                        {[1, 2, 3, 4].map(n => (
                            <div key={n} className="aspect-[4/5] bg-[#001c28]/5 rounded-[2.5rem] animate-pulse border border-[#001c28]/5" />
                        ))}
                    </div>
                ) : filteredSpaces.length === 0 ? (
                    <div className="mt-12">
                        <div className="py-24 text-center bg-white rounded-[2.5rem] border border-[#001c28]/5 shadow-sm">
                            <LayoutGrid className="w-16 h-16 mx-auto text-[#001c28]/20 mb-6" />
                            <h3 className="text-xl font-black text-[#001c28] mb-2 uppercase italic tracking-tighter">Nenhum espaço encontrado</h3>
                            <p className="text-[#001c28]/50 font-medium max-w-sm mx-auto text-sm">
                                {searchTerm
                                    ? "Não encontramos nada com esses termos de busca."
                                    : "Comece criando o primeiro espaço para os usuários poderem agendar."}
                            </p>
                            {!searchTerm && (
                                <button
                                    onClick={() => canAddMore && openForm()}
                                    className="mt-8 text-[#001c28] font-black hover:text-[#d4a94d] transition-colors flex items-center justify-center mx-auto uppercase tracking-wider text-sm hover:underline"
                                >
                                    <Plus className="w-4 h-4 mr-1" /> Criar Espaço Agora
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredSpaces.map(space => (
                            <SpaceCard
                                key={space.id}
                                space={space}
                                isSelected={selectedIds.includes(space.id)}
                                onToggle={toggleSelect}
                                onEdit={openForm}
                                onDelete={handleDeleteSpace}
                            />
                        ))}
                    </div>
                )
            ) : (
                <div className="space-y-4">
                    {globalReviews.length === 0 ? (
                        <div className="text-center py-10 text-gray-400 font-bold uppercase tracking-widest">Nenhuma avaliação encontrada.</div>
                    ) : (
                        globalReviews.map(review => (
                            <div key={review.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-bold text-[#001c28]">{review.userName}</h4>
                                    <div className="text-[#d4a94d] font-black">★ {review.rating}</div>
                                </div>
                                <p className="text-sm text-gray-600 italic">"{review.comment}"</p>
                                <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase">{review.createdAt?.toDate?.().toLocaleDateString()}</p>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Plan Warning */}
            {!canAddMore && (
                <div className="bg-[#001c28] p-6 rounded-[2.5rem] border border-[#001c28]/10 flex items-start space-x-4 shadow-lg shadow-[#001c28]/10">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-[#d4a94d] flex-shrink-0">
                        <Info className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm text-white font-black uppercase tracking-wide mb-1">Limite atingido!</p>
                        <p className="text-white/60 text-xs font-medium">
                            Faça o upgrade da sua organização para adicionar mais espaços e fotos ilimitadas.
                        </p>
                    </div>
                    <button className="text-[#d4a94d] text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors mt-1 flex-shrink-0 border border-[#d4a94d] px-4 py-2 rounded-xl hover:bg-[#d4a94d]">
                        Ver Planos
                    </button>
                </div>
            )}

            {/* Batch Actions Toolbar */}
            {selectedIds.length > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#001c28] text-white px-8 py-4 rounded-full shadow-2xl flex items-center gap-6 animate-in slide-in-from-bottom-4 border border-[#d4a94d]/20">
                    <span className="font-bold text-sm tracking-wide">
                        <span className="text-[#d4a94d] font-black">{selectedIds.length}</span> selecionados
                    </span>
                    <div className="h-4 w-px bg-white/10" />
                    <button
                        onClick={() => {
                            const first = spaces.find(s => s.id === selectedIds[0]) || null
                            setSelectedSpace(first)
                            setIsBatchEdit(true)
                            setIsFormOpen(true)
                        }}
                        className="flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-[#d4a94d] transition-colors"
                    >
                        <Settings2 className="w-4 h-4" />
                        Editar em Lote
                    </button>
                    <button
                        onClick={handleBatchDelete}
                        className="flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-red-500 transition-colors ml-2"
                    >
                        <Trash2 className="w-4 h-4" />
                        Excluir
                    </button>
                    <button
                        onClick={() => setSelectedIds([])}
                        className="flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-red-400 transition-colors ml-2"
                    >
                        <X className="w-4 h-4" />
                        Cancelar
                    </button>
                </div>
            )}

            {/* Modal do Formulário */}
            {isFormOpen && (
                <SpaceForm
                    space={selectedSpace}
                    batchIds={isBatchEdit ? selectedIds : undefined}
                    onClose={() => setIsFormOpen(false)}
                />
            )}
        </div>
    )
}
